import apiClient from './apiClient';
import {
  InterviewTemplate,
  InterviewTemplatePayload,
} from '../types';

const apiBaseURL = '/interview-templates/';

/**
 * Fetches all interview templates.
 * @param filterType Optional filter by template type ('audit' or 'cadrage')
 * @returns A promise that resolves to an array of interview templates.
 */
export const fetchInterviewTemplates = async (filterType?: 'audit' | 'cadrage' | null): Promise<InterviewTemplate[]> => {
  let url = apiBaseURL;
  if (filterType) {
    url += `?template_type=${filterType}`;
  }
  const response = await apiClient.get<InterviewTemplate[]>(url);
  return response.data;
};

/**
 * Fetches a single interview template by its ID.
 * @param id The ID of the interview template.
 * @returns A promise that resolves to the interview template.
 */
export const fetchInterviewTemplateById = async (id: string): Promise<InterviewTemplate> => {
  const response = await apiClient.get<InterviewTemplate>(`${apiBaseURL}${id}`);
  return response.data;
};

/**
 * Creates a new interview template.
 * @param data The data for the new interview template.
 * @returns A promise that resolves to the created interview template.
 */
export const createInterviewTemplate = async (data: InterviewTemplatePayload): Promise<InterviewTemplate> => {
  const response = await apiClient.post<InterviewTemplate>(apiBaseURL, data);
  return response.data;
};

/**
 * Updates an existing interview template.
 * @param id The ID of the interview template to update.
 * @param data The data to update the interview template with (partial allowed).
 * @returns A promise that resolves to the updated interview template.
 */
export const updateInterviewTemplate = async (id: string, data: Partial<InterviewTemplatePayload>): Promise<InterviewTemplate> => {
  const response = await apiClient.put<InterviewTemplate>(`${apiBaseURL}${id}`, data);
  return response.data;
};

/**
 * Deletes an interview template by its ID.
 * @param id The ID of the interview template to delete.
 * @returns A promise that resolves when the template is deleted.
 */
export const deleteInterviewTemplate = async (id: string): Promise<void> => {
  await apiClient.delete(`${apiBaseURL}${id}`);
};

// Service object with all methods
const interviewTemplateService = {
  getAll: fetchInterviewTemplates,
  getById: fetchInterviewTemplateById,
  create: createInterviewTemplate,
  update: updateInterviewTemplate,
  delete: deleteInterviewTemplate,
};

export default interviewTemplateService;
